import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TrendingUpIcon, MinusIcon, TrendingDownIcon, ChevronDownIcon, ChevronUpIcon, SparklesIcon, AlertTriangleIcon } from 'lucide-react';
interface ContractorData {
  rank: number;
  name: string;
  trade: string;
  score: number;
  trend: 'up' | 'stable' | 'down';
  risk: string;
  expanded: {
    hazardCoverage: number;
    controlQuality: number;
    crewParticipation: number;
    meetingQuality: number;
    updateRate: number;
    quizCompletion: number;
    submitted: number;
    trendDetail: string;
    aiInsight: string;
    suggestedAction: string;
  };
}
const CONTRACTORS: ContractorData[] = [{
  rank: 1,
  name: 'SteelCore Erectors',
  trade: 'Structural Steel',
  score: 91,
  trend: 'up',
  risk: 'None',
  expanded: {
    hazardCoverage: 93,
    controlQuality: 90,
    crewParticipation: 84,
    meetingQuality: 89,
    updateRate: 71,
    quizCompletion: 88,
    submitted: 34,
    trendDetail: 'Improved after supervisor coaching and better meeting participation',
    aiInsight: 'Consistently high performance across all PTP dimensions. Strong meeting quality and worker engagement are driving results.',
    suggestedAction: 'Use as benchmark contractor.'
  }
}, {
  rank: 2,
  name: 'Skyline Concrete',
  trade: 'Concrete',
  score: 87,
  trend: 'stable',
  risk: 'None',
  expanded: {
    hazardCoverage: 89,
    controlQuality: 85,
    crewParticipation: 82,
    meetingQuality: 86,
    updateRate: 65,
    quizCompletion: 81,
    submitted: 28,
    trendDetail: 'Stable performance with consistent meeting quality',
    aiInsight: 'Reliable contractor with steady PTP quality. Minor room for improvement in crew participation.',
    suggestedAction: 'Maintain current approach.'
  }
}, {
  rank: 3,
  name: 'NorthEdge Framing',
  trade: 'Framing',
  score: 83,
  trend: 'up',
  risk: 'Watch quiz completion',
  expanded: {
    hazardCoverage: 85,
    controlQuality: 82,
    crewParticipation: 78,
    meetingQuality: 81,
    updateRate: 60,
    quizCompletion: 64,
    submitted: 22,
    trendDetail: 'Improving but quiz completion remains low',
    aiInsight: 'Good trajectory on planning quality but crew understanding verification is lagging.',
    suggestedAction: 'Focus on improving quiz participation rates.'
  }
}, {
  rank: 4,
  name: 'Prime MEP Solutions',
  trade: 'MEP',
  score: 80,
  trend: 'stable',
  risk: 'Low crew engagement',
  expanded: {
    hazardCoverage: 82,
    controlQuality: 79,
    crewParticipation: 68,
    meetingQuality: 80,
    updateRate: 58,
    quizCompletion: 62,
    submitted: 26,
    trendDetail: 'Stable scores but crew engagement metrics are consistently low',
    aiInsight: 'Planning quality is adequate but crew participation in meetings and quizzes suggests limited worker buy-in.',
    suggestedAction: 'Increase crew involvement in PTP discussions.'
  }
}, {
  rank: 5,
  name: 'Titan Exteriors',
  trade: 'Facade / Exterior',
  score: 77,
  trend: 'down',
  risk: 'Declining field follow-through',
  expanded: {
    hazardCoverage: 79,
    controlQuality: 76,
    crewParticipation: 72,
    meetingQuality: 75,
    updateRate: 48,
    quizCompletion: 59,
    submitted: 19,
    trendDetail: 'Declining scores over last two weeks',
    aiInsight: 'Field follow-through on controls is weakening. PTP update rates suggest plans are not being revised after meetings.',
    suggestedAction: 'Schedule field observation to verify PTP implementation.'
  }
}, {
  rank: 6,
  name: 'Metro Fire Systems',
  trade: 'Fire Protection',
  score: 74,
  trend: 'down',
  risk: 'Low meeting quality',
  expanded: {
    hazardCoverage: 76,
    controlQuality: 73,
    crewParticipation: 70,
    meetingQuality: 65,
    updateRate: 45,
    quizCompletion: 58,
    submitted: 15,
    trendDetail: 'Meeting quality has dropped significantly',
    aiInsight: 'Meetings are becoming shorter and less thorough. This is impacting downstream quiz scores and crew understanding.',
    suggestedAction: 'Review meeting facilitation with site supervisor.'
  }
}, {
  rank: 7,
  name: 'BlueLine Interiors',
  trade: 'Interior Systems',
  score: 71,
  trend: 'down',
  risk: 'Declining PTP quality',
  expanded: {
    hazardCoverage: 73,
    controlQuality: 70,
    crewParticipation: 65,
    meetingQuality: 69,
    updateRate: 42,
    quizCompletion: 55,
    submitted: 21,
    trendDetail: 'Consistent decline across all metrics',
    aiInsight: 'Broad decline across planning, meeting, and engagement metrics. Indicates systemic issue with PTP process adoption.',
    suggestedAction: 'Conduct PTP process refresher training.'
  }
}, {
  rank: 8,
  name: 'Rapid Earthworks',
  trade: 'Site / Civil',
  score: 68,
  trend: 'down',
  risk: 'Immediate attention',
  expanded: {
    hazardCoverage: 70,
    controlQuality: 67,
    crewParticipation: 61,
    meetingQuality: 66,
    updateRate: 39,
    quizCompletion: 54,
    submitted: 17,
    trendDetail: 'Hazards are captured inconsistently and crews are not engaging well in meetings or quizzes',
    aiInsight: 'This contractor is underperforming across planning quality, meeting quality, and worker understanding. Low quiz completion and weak meeting engagement indicate poor PTP communication effectiveness.',
    suggestedAction: 'Prioritize field observation and supervisor coaching.'
  }
}];
function getScoreColor(score: number) {
  if (score >= 90) return 'text-green-400';
  if (score >= 80) return 'text-lime-400';
  if (score >= 70) return 'text-amber-400';
  return 'text-red-400';
}
function getTrendIcon(trend: string) {
  if (trend === 'up') return <TrendingUpIcon className="w-4 h-4 text-green-400" data-id="element-197" />;
  if (trend === 'stable') return <MinusIcon className="w-4 h-4 text-slate-400" data-id="element-198" />;
  return <TrendingDownIcon className="w-4 h-4 text-orange-400" data-id="element-199" />;
}
export function DashboardScreen() {
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const toggleRow = (rank: number) => {
    setExpandedRow(expandedRow === rank ? null : rank);
  };
  return <div className="flex-1 overflow-y-auto pb-6 -mx-2 px-2 space-y-4" data-id="element-200">
      {/* Project Summary Card */}
      <div className="bg-slate-900 border border-orange-500/30 ring-1 ring-orange-500/10 rounded-xl p-4" data-id="element-201">
        <div className="flex items-center justify-between mb-3" data-id="element-202">
          <span className="text-[10px] text-orange-400 font-bold uppercase tracking-wider" data-id="element-203">
            Project Overview
          </span>
          <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider" data-id="element-204">
            Last 14 Days
          </span>
        </div>

        <div className="mb-4" data-id="element-205">
          <h3 className="text-white font-bold text-lg" data-id="element-206">
            Downtown Office Tower
          </h3>
          <p className="text-slate-400 text-sm" data-id="element-207">Structural + Envelope</p>
        </div>

        <div className="mb-5" data-id="element-208">
          <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-1" data-id="element-209">
            Average PTP Quality Score
          </p>
          <div className="flex items-center gap-3" data-id="element-210">
            <span className="text-4xl font-bold text-amber-400" data-id="element-211">79</span>
            <div className="flex items-center gap-1 bg-orange-500/10 text-orange-400 px-2 py-1 rounded-md text-xs font-medium" data-id="element-212">
              <TrendingDownIcon className="w-3 h-3" data-id="element-213" />
              Downward
            </div>
          </div>
          <p className="text-slate-400 text-xs mt-2 leading-snug" data-id="element-214">
            Driven by declining meeting quality and quiz completion in
            lower-performing contractors
          </p>
        </div>

        <div className="grid grid-cols-2 gap-y-3 gap-x-4 pt-3 border-t border-slate-800" data-id="element-215">
          <div data-id="element-216">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold" data-id="element-217">
              Total PTPs
            </p>
            <p className="text-white font-medium text-sm" data-id="element-218">186</p>
          </div>
          <div data-id="element-219">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold" data-id="element-220">
              Active Contractors
            </p>
            <p className="text-white font-medium text-sm" data-id="element-221">8</p>
          </div>
          <div data-id="element-222">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold" data-id="element-223">
              Avg Meeting Quality
            </p>
            <p className="text-white font-medium text-sm" data-id="element-224">77</p>
          </div>
          <div data-id="element-225">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold" data-id="element-226">
              Avg Quiz Completion
            </p>
            <p className="text-white font-medium text-sm" data-id="element-227">69%</p>
          </div>
        </div>
      </div>

      <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider pt-2" data-id="element-228">
        Contractor Performance
      </h3>

      {/* Contractor List */}
      <div className="space-y-2.5" data-id="element-229">
        {CONTRACTORS.map(contractor => {
        const isExpanded = expandedRow === contractor.rank;
        const scoreColor = getScoreColor(contractor.score);
        return <div key={contractor.rank} className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden" data-id="element-230">
              <button onClick={() => toggleRow(contractor.rank)} className="w-full p-4 flex items-center justify-between text-left focus:outline-none" data-id="element-231">
                <div className="flex-1 min-w-0 pr-4" data-id="element-232">
                  <div className="flex items-baseline gap-2 mb-0.5" data-id="element-233">
                    <span className="text-slate-500 font-mono text-xs" data-id="element-234">
                      {contractor.rank}.
                    </span>
                    <h4 className="text-white font-bold text-sm truncate" data-id="element-235">
                      {contractor.name}
                    </h4>
                  </div>
                  <p className="text-slate-500 text-xs mb-2 pl-4" data-id="element-236">
                    {contractor.trade}
                  </p>

                  <div className="flex items-center gap-3 pl-4 flex-wrap" data-id="element-237">
                    <div className="flex items-center gap-1.5" data-id="element-238">
                      <span className={`text-lg font-bold ${scoreColor}`} data-id="element-239">
                        {contractor.score}
                      </span>
                      {getTrendIcon(contractor.trend)}
                    </div>

                    {contractor.risk !== 'None' && <div className={`flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${contractor.risk === 'Immediate attention' ? 'bg-red-500/10 text-red-400' : 'bg-amber-500/10 text-amber-400'}`} data-id="element-240">
                        <AlertTriangleIcon className="w-3 h-3" data-id="element-241" />
                        <span className="truncate max-w-[120px]" data-id="element-242">
                          {contractor.risk}
                        </span>
                      </div>}
                  </div>
                </div>

                <div className="text-slate-500 flex-shrink-0" data-id="element-243">
                  {isExpanded ? <ChevronUpIcon className="w-5 h-5" data-id="element-244" /> : <ChevronDownIcon className="w-5 h-5" data-id="element-245" />}
                </div>
              </button>

              <AnimatePresence data-id="element-246">
                {isExpanded && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.25,
              ease: 'easeInOut'
            }} className="overflow-hidden" data-id="element-247">
                    <div className="px-4 pb-4 pt-2 border-t border-slate-800/50" data-id="element-248">
                      {/* Metrics Grid */}
                      <div className="grid grid-cols-2 gap-y-3 gap-x-4 mb-4" data-id="element-249">
                        <div data-id="element-250">
                          <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold" data-id="element-251">
                            Hazard Coverage
                          </p>
                          <p className="text-white font-medium text-sm" data-id="element-252">
                            {contractor.expanded.hazardCoverage}
                          </p>
                        </div>
                        <div data-id="element-253">
                          <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold" data-id="element-254">
                            Control Quality
                          </p>
                          <p className="text-white font-medium text-sm" data-id="element-255">
                            {contractor.expanded.controlQuality}
                          </p>
                        </div>
                        <div data-id="element-256">
                          <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold" data-id="element-257">
                            Meeting Quality
                          </p>
                          <p className="text-white font-medium text-sm" data-id="element-258">
                            {contractor.expanded.meetingQuality}
                          </p>
                        </div>
                        <div data-id="element-259">
                          <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold" data-id="element-260">
                            Crew Participation
                          </p>
                          <p className="text-white font-medium text-sm" data-id="element-261">
                            {contractor.expanded.crewParticipation}
                          </p>
                        </div>
                        <div data-id="element-262">
                          <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold" data-id="element-263">
                            Quiz Completion
                          </p>
                          <p className="text-white font-medium text-sm" data-id="element-264">
                            {contractor.expanded.quizCompletion}%
                          </p>
                        </div>
                        <div data-id="element-265">
                          <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold" data-id="element-266">
                            Update Rate
                          </p>
                          <p className="text-white font-medium text-sm" data-id="element-267">
                            {contractor.expanded.updateRate}%
                          </p>
                        </div>
                        <div data-id="element-268">
                          <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold" data-id="element-269">
                            PTPs Submitted
                          </p>
                          <p className="text-white font-medium text-sm" data-id="element-270">
                            {contractor.expanded.submitted}
                          </p>
                        </div>
                      </div>

                      {/* Trend Detail */}
                      <div className="mb-4" data-id="element-271">
                        <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-1" data-id="element-272">
                          Trend Detail
                        </p>
                        <p className="text-slate-300 text-sm leading-snug" data-id="element-273">
                          {contractor.expanded.trendDetail}
                        </p>
                      </div>

                      {/* AI Insight */}
                      <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-3 mb-3" data-id="element-274">
                        <div className="flex items-center gap-1.5 mb-1.5" data-id="element-275">
                          <SparklesIcon className="w-3.5 h-3.5 text-orange-400" data-id="element-276" />
                          <p className="text-[10px] text-orange-400 uppercase tracking-wider font-bold" data-id="element-277">
                            AI Insight
                          </p>
                        </div>
                        <p className="text-orange-100 text-sm leading-relaxed" data-id="element-278">
                          {contractor.expanded.aiInsight}
                        </p>
                      </div>

                      {/* Suggested Action */}
                      <div className="bg-slate-950/50 rounded-lg p-3" data-id="element-279">
                        <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-1" data-id="element-280">
                          Suggested Action
                        </p>
                        <p className="text-white text-sm font-medium" data-id="element-281">
                          {contractor.expanded.suggestedAction}
                        </p>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>;
      })}
      </div>
    </div>;
}